import type { ClientFlowAction } from '~/graphql/types.client';

/**
 * Topologically sorts actions so that parents appear before their children.
 * Actions with no in-branch parents (roots) come first.
 */
const sortTopological = (
  actions: Array<ClientFlowAction>,
  branchIds: Set<string>,
): Array<ClientFlowAction> => {
  // Track actions by id so we can rebuild the final ordered list.
  const actionMap = new Map<string, ClientFlowAction>();
  // Count how many in-branch parents each action still depends on.
  const inDegree = new Map<string, number>();
  // Build an adjacency list of parent -> children for in-branch links.
  const childrenOf = new Map<string, Array<string>>();

  for (const action of actions) {
    actionMap.set(action.id, action);
    inDegree.set(action.id, 0);
    childrenOf.set(action.id, []);
  }

  for (const action of actions) {
    if (action.__typename !== 'FlowV2_Action_Start' && 'parentIds' in action) {
      for (const parentId of action.parentIds) {
        // Only relationships inside the copied branch affect ordering.
        if (parentId && branchIds.has(parentId)) {
          childrenOf.get(parentId)?.push(action.id);
          inDegree.set(action.id, (inDegree.get(action.id) ?? 0) + 1);
        }
      }
    }
  }

  // Start with roots: actions that have no parents inside this branch.
  const queue: Array<string> = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  // Build the final order by repeatedly taking the next action whose
  // in-branch dependencies have all been satisfied.
  const sorted: Array<ClientFlowAction> = [];
  while (queue.length > 0) {
    // queue.length > 0 guarantees shift() is non-null — assert to avoid the
    // silent no-op that `id ?? ''` would produce on an actionMap miss.
    const id = queue.shift()!;
    const action = actionMap.get(id);
    if (action) {
      // Once an action is ready, append it to the result before unlocking any
      // of its children.
      sorted.push(action);
    }

    // "Remove" this node from the graph by decreasing each child's remaining
    // parent count; once a child reaches zero it is ready to be processed.
    for (const childId of childrenOf.get(id) ?? []) {
      const newDeg = (inDegree.get(childId) ?? 1) - 1;
      inDegree.set(childId, newDeg);

      // A child enters the queue exactly once: when all of its in-branch
      // parents have already been added to the sorted output.
      if (newDeg === 0) queue.push(childId);
    }
  }

  if (sorted.length !== actions.length) {
    throw new Error('Cycle detected in branch — cannot topologically sort');
  }

  return sorted;
};

export default sortTopological;
